import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import {
  clearAllDrawings,
  setSymbol,
  setTabWeeklyBehaviour,
  setTimeframe,
} from "@/state/slices/uiSlice";
import { createTab } from "@/state/slices/tabsSlice";
import { allSymbols, defaultTfFor } from "@/utils/symbolMeta";
import type { Timeframe } from "@/api/types";
import styles from "./Header.module.css";

const ChevronDown = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
    <path
      d="M2 4 L5 7 L8 4"
      stroke="currentColor"
      strokeWidth="1.4"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** The Header's symbol chip → a dropdown over the curated `/api/symbols`
 *  list. Selecting dispatches `setSymbol` for the active tab; everything
 *  downstream (OHLC, ticks, drawings, precision, session model) follows.
 *
 *  Guard: drawings + Weekly Behaviour highlights are made at one
 *  instrument's price levels and are nonsense at another's. If the tab
 *  carries any, a confirm dialog offers "open in a new tab" (keep the
 *  work) or "clear & switch here" (wipe it). Either way the workspace
 *  autosave persists the result — drawings, weeklyBehaviour, symbol and
 *  the tab list are all part of its change-hash, so the server PUT fires
 *  on its own (content ~10s debounce, new-tab structural ~1.5s, plus the
 *  page-hide flush). */
export function SymbolPicker({
  tabId,
  symbol,
}: {
  tabId: string;
  symbol: string;
}) {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  // Symbol awaiting confirmation because this chart has drawings/highlights.
  const [pending, setPending] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Chart-specific work that won't line up with another instrument's
  // price levels: any drawing, or a Weekly Behaviour customization.
  const tabUi = useAppSelector((s) => s.ui.byTab[tabId]);
  const hasChartWork =
    !!tabUi &&
    (tabUi.fibRetracements.length > 0 ||
      tabUi.horizontalLines.length > 0 ||
      tabUi.verticalLines.length > 0 ||
      tabUi.diagonalLines.length > 0 ||
      tabUi.paths.length > 0 ||
      tabUi.positions.length > 0 ||
      tabUi.weeklyBehaviour != null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Escape cancels the confirm dialog.
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPending(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [pending]);

  // Loaded once at app boot. Fall back to just the active symbol if the
  // metadata fetch hasn't resolved yet.
  const loaded = allSymbols();
  const list = loaded.length
    ? loaded
    : [{ symbol, name: symbol, digits: 3, session_type: "" }];

  const switchHere = (s: string) => {
    dispatch(setSymbol({ tabId, symbol: s }));
    // Open the symbol at its configured default timeframe (e.g. BTC → 1D).
    dispatch(setTimeframe({ tabId, timeframe: defaultTfFor(s) as Timeframe }));
  };

  const pick = (s: string) => {
    setOpen(false);
    if (s === symbol) return;
    if (hasChartWork) {
      setPending(s); // confirm before destroying / relocating the work
      return;
    }
    switchHere(s);
  };

  const clearAndSwitch = () => {
    if (!pending) return;
    dispatch(clearAllDrawings({ tabId }));
    dispatch(setTabWeeklyBehaviour({ tabId, weeklyBehaviour: null }));
    switchHere(pending);
    setPending(null);
  };

  const openInNewTab = () => {
    if (!pending) return;
    const newId = dispatch(createTab(pending)); // labelled, activates itself
    dispatch(setSymbol({ tabId: newId, symbol: pending }));
    dispatch(
      setTimeframe({ tabId: newId, timeframe: defaultTfFor(pending) as Timeframe }),
    );
    setPending(null);
  };

  return (
    <div className={styles.symbolPicker} ref={ref}>
      <button
        type="button"
        className={styles.symbolChip}
        onClick={() => setOpen((o) => !o)}
        title="Select symbol"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={styles.goldDot} />
        {symbol}
        <ChevronDown />
      </button>
      {open ? (
        <div className={styles.symbolMenu} role="listbox">
          {list.map((m) => (
            <button
              key={m.symbol}
              type="button"
              role="option"
              aria-selected={m.symbol === symbol}
              className={`${styles.symbolMenuItem} ${
                m.symbol === symbol ? styles.symbolMenuItemActive : ""
              }`}
              onClick={() => pick(m.symbol)}
            >
              <span className={styles.symbolMenuTicker}>{m.symbol}</span>
              <span className={styles.symbolMenuName}>{m.name}</span>
            </button>
          ))}
        </div>
      ) : null}
      {pending ? (
        <div
          className={styles.symbolConfirmBackdrop}
          onClick={() => setPending(null)}
        >
          <div
            className={styles.symbolConfirm}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.symbolConfirmTitle}>Switch to {pending}?</div>
            <p className={styles.symbolConfirmBody}>
              This chart has drawings or highlights made for {symbol}. They
              won&apos;t line up with {pending} prices.
            </p>
            <div className={styles.symbolConfirmActions}>
              <button
                type="button"
                className={styles.symbolConfirmPrimary}
                onClick={openInNewTab}
              >
                Open {pending} in a new tab
              </button>
              <button
                type="button"
                className={styles.symbolConfirmDanger}
                onClick={clearAndSwitch}
              >
                Clear drawings &amp; switch here
              </button>
              <button
                type="button"
                className={styles.symbolConfirmGhost}
                onClick={() => setPending(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
